import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { store } from '../../store';
import { router } from '../../router';
import { api } from '../../api';
import { t } from '../../i18n';
import { icon } from '../ui/icons';

/**
 * <cadence-user-menu logout-path="/login"> — avatar button that opens a dropdown
 * with account info and log out. Used by the admin header and the portal nav.
 * (Language switching lives in <cadence-lang-menu>, next to the theme toggle.)
 */
@customElement('cadence-user-menu')
export class CadenceUserMenu extends LitElement {
  @property({ attribute: 'logout-path' }) logoutPath = '/login';
  @state() private open = false;

  static styles = css`
    :host { position: relative; display: inline-block; }
    .trigger {
      display: flex; align-items: center; gap: 8px; cursor: pointer;
      background: none; border: none; padding: 4px; border-radius: var(--radius-md); color: inherit; font: inherit;
    }
    .trigger:hover { background: var(--color-bg-secondary); }
    .who { display: flex; flex-direction: column; line-height: 1.2; text-align: left; }
    .who .name { font-weight: 600; font-size: 0.9rem; color: var(--color-text-primary); }
    .who .role { color: var(--color-text-secondary); font-size: 0.78rem; }
    .chevron { color: var(--color-text-secondary); transition: transform 0.15s; }
    .chevron.open { transform: rotate(180deg); }
    @media (max-width: 768px) { .who { display: none; } }

    .backdrop { position: fixed; inset: 0; z-index: 40; }
    .menu {
      position: absolute; top: calc(100% + 8px); right: 0; z-index: 50; min-width: 220px;
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-md); box-shadow: var(--shadow-lg); overflow: hidden;
      padding: 6px;
    }
    .head { padding: 8px 10px 10px; border-bottom: 1px solid var(--color-border); margin-bottom: 6px; }
    .head .name { font-weight: 600; font-size: 0.92rem; }
    .head .email { color: var(--color-text-secondary); font-size: 0.8rem; word-break: break-all; }
    .item {
      display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
      background: none; border: none; cursor: pointer; font: inherit; color: var(--color-text-primary);
      padding: 9px 10px; border-radius: var(--radius-sm);
    }
    .item:hover { background: var(--color-bg-secondary); }
    .item.danger { color: var(--color-danger); }
    .item svg { width: 16px; height: 16px; }
  `;

  private toggle() { this.open = !this.open; }
  private close() { this.open = false; }

  private async logout() {
    this.close();
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    store.set({ user: null });
    router.navigate(this.logoutPath);
  }

  render() {
    const user = store.get().user;
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '';
    return html`
      <button class="trigger" aria-haspopup="menu" aria-expanded=${this.open ? 'true' : 'false'}
        aria-label=${t('common.header.userMenu')} @click=${() => this.toggle()}>
        <cadence-avatar name=${name} src=${user?.profile_photo_url ?? ''} size="34"></cadence-avatar>
        <span class="who">
          <span class="name">${name}</span>
          <span class="role">${user?.role?.name ?? ''}</span>
        </span>
        <span class="chevron ${this.open ? 'open' : ''}">${icon('chevron-down', 16)}</span>
      </button>
      ${this.open ? html`
        <div class="backdrop" @click=${() => this.close()}></div>
        <div class="menu" role="menu">
          <div class="head">
            <div class="name">${name}</div>
            ${user?.email ? html`<div class="email">${user.email}</div>` : ''}
          </div>
          <button class="item danger" role="menuitem" @click=${() => this.logout()}>
            ${t('common.actions.logout')}
          </button>
        </div>` : ''}
    `;
  }
}
